/**
 * Pure helpers for job-page SEO. No Next.js imports so tests can run them
 * outside the app.
 */

import { toIsoCountry } from "./countryIso";

export const SITE_URL = "https://maritime.10ure.com";

export const LOGO_CONTENT_SECURITY_POLICY = "default-src 'none'; sandbox";

export const ALLOWED_LOGO_CONTENT_TYPES = [
  "image/gif",
  "image/jpeg",
  "image/png",
  "image/webp",
];

export const PRIVATE_PATHS = ["/dashboard", "/jobs/applied"] as const;

export interface JobSeoInput {
  category?: string;
  city?: string;
  country?: string;
  description?: string;
  employer?: string;
  employerLogoUrl?: string;
  employmentType?: string;
  postedDate?: string;
  refId?: string;
  remote?: boolean;
  validThrough?: string;
  state?: string;
  title?: string;
}

export interface SitemapEntry {
  lastmod?: string;
  loc: string;
}

const EMPLOYMENT_TYPES: Record<string, string> = {
  contract: "CONTRACTOR",
  contractor: "CONTRACTOR",
  "full time": "FULL_TIME",
  full_time: "FULL_TIME",
  "full-time": "FULL_TIME",
  fulltime: "FULL_TIME",
  intern: "INTERN",
  internship: "INTERN",
  other: "OTHER",
  "part time": "PART_TIME",
  part_time: "PART_TIME",
  "part-time": "PART_TIME",
  parttime: "PART_TIME",
  "per diem": "PER_DIEM",
  per_diem: "PER_DIEM",
  perdiem: "PER_DIEM",
  temp: "TEMPORARY",
  temporary: "TEMPORARY",
  volunteer: "VOLUNTEER",
};

export function cleanText(value?: string | null): string | undefined {
  if (!value) {
    return undefined;
  }
  const trimmed = value.replace(/\s+/g, " ").trim();
  return trimmed.length ? trimmed : undefined;
}

export function normalizeDate(value?: string | null): string | undefined {
  if (!value) {
    return undefined;
  }
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(value.trim());
  if (!match || match[1].startsWith("0000") || match[1].startsWith("0001")) {
    return undefined;
  }
  return match[1];
}

export function toJobSeoInput(job: any): JobSeoInput {
  if (!job || typeof job !== "object") {
    return {};
  }
  return {
    category: cleanText(job.category),
    city: cleanText(job.city),
    country: cleanText(job.country),
    description:
      typeof job.description === "string" ? job.description : undefined,
    employer: cleanText(job.employer),
    employerLogoUrl: cleanText(job.employerLogoUrl || job.employer_logo_url),
    employmentType: cleanText(job.employmentType || job.employment_type),
    postedDate: normalizeDate(job.postedDate || job.posted_date),
    refId: cleanText(job.refId || job.ref_id),
    remote: isRemoteJob(job),
    state: cleanText(job.state),
    title: cleanText(job.title),
    validThrough: buildValidThrough(
      job.validThrough ||
        job.valid_through ||
        job.expiresAt ||
        job.expires_at ||
        job.expirationDate ||
        job.expiration_date
    ),
  };
}

function isRemoteJob(job: any): boolean {
  const flag = job.remote || job.isRemote || job.is_remote;
  const locationType = String(
    job.jobLocationType || job.job_location_type || ""
  ).toUpperCase();
  if (flag === true || locationType === "TELECOMMUTE") {
    return true;
  }
  return [job.city, job.state, job.country].some((part) => isRemoteText(part));
}

function isRemoteText(value?: string): boolean {
  if (!value) {
    return false;
  }
  return /\bremote\b|work from home|telecommut/i.test(value);
}

export function jobDetailPath(refId: string): string {
  return `/jobs/${refId}/view`;
}

export function jobDetailUrl(
  refId: string,
  siteUrl: string = SITE_URL
): string {
  return `${siteUrl.replace(/\/$/, "")}${jobDetailPath(refId)}`;
}

export function buildJobPageTitle(job: JobSeoInput): string {
  const title = job.title || "Maritime job";
  const parts = [title];
  if (job.category) {
    parts.push(job.category);
  }
  if (job.city && !isRemoteText(job.city)) {
    parts.push(job.city);
  }
  if (job.employer) {
    parts.push(job.employer);
  }
  return `${parts.join(" – ")} | 10ure Jobs`;
}

export function absolutePageUrl(
  asPath?: string,
  siteUrl: string = SITE_URL
): string {
  const path = (asPath || "/").split("?")[0] || "/";
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${siteUrl.replace(/\/$/, "")}${normalized}`;
}

export function buildJobMetaDescription(job: JobSeoInput): string {
  const title = job.title || "Maritime job";
  const place = [job.city, job.state, job.country].filter(Boolean).join(", ");
  const lead = [
    title,
    job.employer ? `at ${job.employer}` : "",
    place ? `in ${place}` : "",
  ]
    .filter(Boolean)
    .join(" ");
  const blurb = firstSentences(job.description);
  const text = blurb ? `${lead}. ${blurb}` : `${lead}. Apply on 10ure Jobs.`;
  return truncate(text, 160);
}

/**
 * Only a real expiry from the API. Posted date is not an expiry: adding two
 * months made every older listing look expired to Google.
 */
export function buildValidThrough(expiry?: string | null): string | undefined {
  return normalizeDate(expiry);
}

export function schemaEmploymentType(value?: string): string {
  if (!value) {
    return "OTHER";
  }
  const compact = value.trim().toLowerCase();
  const spaced = compact.replace(/[_-]+/g, " ").replace(/\s+/g, " ");
  return EMPLOYMENT_TYPES[compact] || EMPLOYMENT_TYPES[spaced] || "OTHER";
}

export function isSignedUrl(url: string): boolean {
  return /(?:Expires=|GoogleAccessId=|X-Goog-Algorithm=|X-Goog-Expires=|X-Goog-Signature=|[?&]Signature=)/.test(
    url
  );
}

const LOGO_HOSTS = new Set([
  "storage.cloud.google.com",
  "storage.googleapis.com",
]);

const LOGO_BUCKETS = ["10ure", "10ure-dev-public"];

export function isAllowedLogoUrl(raw?: string): boolean {
  if (!raw) {
    return false;
  }
  try {
    const url = new URL(raw);
    if (url.protocol !== "https:" || !LOGO_HOSTS.has(url.hostname)) {
      return false;
    }
    return LOGO_BUCKETS.some(
      (bucket) =>
        url.pathname === `/${bucket}` || url.pathname.startsWith(`/${bucket}/`)
    );
  } catch (e) {
    return false;
  }
}

export function isAllowedLogoContentType(contentType?: string | null): boolean {
  if (!contentType) {
    return false;
  }
  const base = contentType.split(";")[0].trim().toLowerCase();
  return ALLOWED_LOGO_CONTENT_TYPES.indexOf(base) !== -1;
}

/**
 * Signed storage URLs expire (the live board signs them for about a week).
 * The schema points at a stable URL on this site that proxies the current file.
 */
export function schemaLogoUrl(
  refId?: string,
  logoUrl?: string,
  siteUrl: string = SITE_URL
): string | undefined {
  if (!refId || !logoUrl || !isAllowedLogoUrl(logoUrl)) {
    return undefined;
  }
  return `${siteUrl.replace(/\/$/, "")}/api/jobs/${encodeURIComponent(
    refId
  )}/logo`;
}

export function buildJobPosting(
  job: JobSeoInput,
  options?: { listed?: boolean; siteUrl?: string }
): Record<string, unknown> | null {
  if (options?.listed === false) {
    return null;
  }
  if (!job.title || !job.refId || !job.postedDate || !job.employer) {
    return null;
  }
  const siteUrl = options?.siteUrl || SITE_URL;
  const logo = schemaLogoUrl(job.refId, job.employerLogoUrl, siteUrl);
  const validThrough = job.validThrough;
  const hiringOrganization: Record<string, string> = {
    "@type": "Organization",
    name: job.employer,
  };
  if (logo) {
    hiringOrganization.logo = logo;
  }
  const posting: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    datePosted: job.postedDate,
    description: descriptionHtml(job.description),
    employmentType: schemaEmploymentType(job.employmentType),
    hiringOrganization,
    title: job.title,
  };
  if (validThrough) {
    posting.validThrough = validThrough;
  }
  const isoCountry = toIsoCountry(job.country);
  if (job.remote && isoCountry) {
    posting.applicantLocationRequirements = {
      "@type": "Country",
      name: isoCountry,
    };
    posting.jobLocationType = "TELECOMMUTE";
  } else {
    const address: Record<string, string> = {
      "@type": "PostalAddress",
    };
    if (isoCountry) {
      address.addressCountry = isoCountry;
    }
    if (job.city && !isRemoteText(job.city)) {
      address.addressLocality = job.city;
    }
    if (job.state && !isRemoteText(job.state)) {
      address.addressRegion = job.state;
    }
    if (
      address.addressCountry ||
      address.addressLocality ||
      address.addressRegion
    ) {
      posting.jobLocation = {
        "@type": "Place",
        address,
      };
    }
  }
  return posting;
}

export function jsonLdScript(data: unknown): string {
  return JSON.stringify(data).replace(/</g, "\\u003c");
}

export function isPrivatePath(pathname: string): boolean {
  const path = pathname.split("?")[0];
  return PRIVATE_PATHS.some(
    (blocked) => path === blocked || path.startsWith(`${blocked}/`)
  );
}

export function sitemapEntriesFromJobs(
  jobs: Array<{ postedDate?: string; refId: string }>,
  siteUrl: string = SITE_URL
): SitemapEntry[] {
  const root = siteUrl.replace(/\/$/, "");
  const seen = new Set<string>();
  const jobEntries: SitemapEntry[] = [];
  let newest: string | undefined;
  jobs.forEach((job) => {
    if (!job.refId || seen.has(job.refId)) {
      return;
    }
    seen.add(job.refId);
    const lastmod = normalizeDate(job.postedDate);
    if (lastmod && (!newest || lastmod > newest)) {
      newest = lastmod;
    }
    const loc = jobDetailUrl(job.refId, root);
    jobEntries.push(lastmod ? { lastmod, loc } : { loc });
  });
  const staticEntries: SitemapEntry[] = [
    newest ? { lastmod: newest, loc: root } : { loc: root },
    newest ? { lastmod: newest, loc: `${root}/jobs` } : { loc: `${root}/jobs` },
    { loc: `${root}/news` },
    { loc: `${root}/support` },
  ];
  return staticEntries.concat(jobEntries).filter((entry) => {
    const path = entry.loc.replace(root, "") || "/";
    return !isPrivatePath(path);
  });
}

export function buildSitemapXml(entries: SitemapEntry[]): string {
  const urls = entries
    .map((entry) => {
      const lastmod = entry.lastmod
        ? `<lastmod>${escapeXml(entry.lastmod)}</lastmod>`
        : "";
      return `<url><loc>${escapeXml(entry.loc)}</loc>${lastmod}</url>`;
    })
    .join("");
  return (
    `<?xml version="1.0" encoding="UTF-8"?>` +
    `<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">` +
    urls +
    `</urlset>`
  );
}

export function escapeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function descriptionHtml(description?: string): string {
  const text = description || "Maritime job opening on 10ure Jobs.";
  const paragraphs = text
    .split(/\n+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => `<p>${escapeHtmlText(part)}</p>`);
  return paragraphs.length
    ? paragraphs.join("")
    : "<p>Maritime job opening on 10ure Jobs.</p>";
}

function escapeHtmlText(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function firstSentences(description?: string): string | undefined {
  const flat = cleanText(description);
  if (!flat) {
    return undefined;
  }
  const match = /^(.+?[.!?])(?:\s|$)/.exec(flat);
  return match ? match[1] : flat;
}

function truncate(value: string, max: number): string {
  if (value.length <= max) {
    return value;
  }
  const sliced = value.slice(0, max - 1);
  const lastSpace = sliced.lastIndexOf(" ");
  const base = lastSpace > 80 ? sliced.slice(0, lastSpace) : sliced;
  return `${base.replace(/[.,;:\s]+$/, "")}…`;
}
